import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Loader2, AlertTriangle, CheckCircle2, AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';
import { api } from '../lib/api';
import { ICON } from './ui';

interface MyrostarConflict {
  id: number;
  shift_date: string;
  conflict_type: string;
  screenshot_start: string;
  screenshot_end: string;
  existing_start: string | null;
  existing_end: string | null;
  existing_source: string | null;
}

interface MyrostarImportResult {
  myrostar_import_id: number;
  shifts_created: number;
  shifts_duplicate: number;
  shifts_skipped: number;
  conflicts: MyrostarConflict[];
}

const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * "Verwerk naar shifts"-knop voor een Myrostar-documentrij op de Documenten-pagina.
 *
 * Bij klik: haal op het laatste moment de parsed_json van het document op
 * (die moet een `shifts`-array bevatten die Claude Code via de Read-tool
 * visueel uit het screenshot heeft geëxtraheerd en via /inbox heeft opgeslagen)
 * en POST naar /api/imports/myrostar. Toont inline created/dup/skip/conflict.
 */
export function MyrostarImportButton({
  documentId,
  compact = true,
}: {
  documentId: number;
  compact?: boolean;
}) {
  const qc = useQueryClient();
  const [lastResult, setLastResult] = useState<MyrostarImportResult | null>(null);

  const mut = useMutation({
    mutationFn: async (): Promise<MyrostarImportResult> => {
      const doc = await api.documents.get(documentId);
      const parsed = doc?.parsed_json;
      const shifts = parsed && Array.isArray((parsed as any).shifts) ? (parsed as any).shifts : null;
      if (!shifts || shifts.length === 0) {
        throw new Error(
          'Er zijn geen diensten uit deze schermafbeelding gehaald. Open het document en vul de diensten aan.'
        );
      }
      const payload = {
        document_id: documentId,
        year: (parsed as any)?.year ?? null,
        week: (parsed as any)?.week ?? null,
        from_date: (parsed as any)?.from_date ?? null,
        to_date: (parsed as any)?.to_date ?? null,
        source: 'myrostar_screenshot',
        shifts: shifts.map((s: any) => ({
          date: s.date,
          day_abbr: s.day_abbr ?? null,
          start: s.start ?? s.start_time,
          end: s.end ?? s.end_time,
          object_name: s.object_name ?? s.object ?? null,
          location: s.location ?? null,
          status: s.status ?? null,
        })),
      };
      const resp = await fetch(`${BASE}/api/imports/myrostar`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) throw new Error(`${resp.status}: ${await resp.text()}`);
      return resp.json();
    },
    onSuccess: (r) => {
      setLastResult(r);
      qc.invalidateQueries({ queryKey: ['shifts'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['calendar'] });
    },
  });

  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (mut.isPending) return;
    mut.mutate();
  };

  const btnClass = compact
    ? 'btn-secondary btn-sm'
    : 'btn-primary';

  return (
    <div className="inline-flex flex-wrap items-center gap-2">
      <button
        type="button"
        className={btnClass}
        onClick={onClick}
        disabled={mut.isPending}
        title="Zet de diensten uit deze schermafbeelding in je rooster"
      >
        {mut.isPending ? (
          <Loader2 className={cn(ICON.sm, 'animate-spin')} aria-hidden />
        ) : (
          <CalendarClock className={ICON.sm} aria-hidden />
        )}
        {compact ? 'Naar rooster' : 'Diensten in rooster zetten'}
      </button>

      {lastResult && <MyrostarImportStatus result={lastResult} />}
      {mut.isError && (
        <span className="inline-flex items-center gap-1.5 text-sm text-red-300" title={(mut.error as Error).message}>
          <AlertCircle className={ICON.sm} aria-hidden /> Mislukt
        </span>
      )}
    </div>
  );
}

function MyrostarImportStatus({ result }: { result: MyrostarImportResult }) {
  const hasConflicts = result.conflicts.length > 0;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-sm',
        hasConflicts ? 'text-amber-200' : 'text-money'
      )}
      title={
        hasConflicts
          ? result.conflicts
              .map(
                (c) =>
                  `${c.shift_date}: rooster ${c.screenshot_start}-${c.screenshot_end}, in app ${c.existing_start ?? '?'}-${c.existing_end ?? '?'}`
              )
              .join('\n')
          : `${result.shifts_created} nieuw, ${result.shifts_duplicate} stond er al`
      }
    >
      {hasConflicts ? <AlertTriangle className={ICON.sm} aria-hidden /> : <CheckCircle2 className={ICON.sm} aria-hidden />}
      {result.shifts_created} nieuw
      {result.shifts_duplicate > 0 && `, ${result.shifts_duplicate} al aanwezig`}
      {result.shifts_skipped > 0 && `, ${result.shifts_skipped} overgeslagen`}
      {hasConflicts && `, ${result.conflicts.length} wijkt af`}
    </span>
  );
}
